interface RenderOptions {
  mode?: 'auto' | 'syntax' | 'markdown-preview' | 'markdown-source';
  language?: string;
  filename?: string;
  showLineNumbers?: boolean;
}

function sanitizeRenderedHtml(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html;

  template.content.querySelectorAll('script, style, iframe, object, embed, form, input, button').forEach((node) => {
    node.remove();
  });

  template.content.querySelectorAll('*').forEach((node) => {
    for (const attr of Array.from(node.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (name.startsWith('on')) {
        node.removeAttribute(attr.name);
        continue;
      }
      if (name === 'style' || name === 'srcdoc') {
        node.removeAttribute(attr.name);
        continue;
      }
      if ((name === 'href' || name === 'src' || name === 'xlink:href') && value.startsWith('javascript:')) {
        node.removeAttribute(attr.name);
      }
    }
  });

  return template.innerHTML;
}

class ContentRenderer {
  private static readonly PREF_KEY = 'skillfab:markdown-display-mode';
  private static readonly MAX_LINES_FOR_LINE_NUMBERS = 10000;

  getMarkdownPreference(): 'preview' | 'source' {
    try {
      const pref = localStorage.getItem(ContentRenderer.PREF_KEY);
      return pref === 'source' ? 'source' : 'preview';
    } catch {
      return 'preview';
    }
  }

  setMarkdownPreference(mode: 'preview' | 'source'): void {
    try {
      localStorage.setItem(ContentRenderer.PREF_KEY, mode);
    } catch (e) {
      console.warn('[ContentRenderer] Failed to save preference:', e);
    }
  }

  private detectLanguage(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    const langMap: Record<string, string> = {
      'py': 'python', 'js': 'javascript', 'ts': 'typescript',
      'tsx': 'tsx', 'jsx': 'jsx', 'json': 'json',
      'yaml': 'yaml', 'yml': 'yaml', 'sh': 'bash',
      'bash': 'bash', 'md': 'markdown', 'html': 'markup',
      'xml': 'markup', 'css': 'css', 'scss': 'scss',
      'sql': 'sql', 'c': 'c', 'cpp': 'cpp', 'cc': 'cpp',
      'go': 'go', 'rs': 'rust', 'java': 'java',
      'rb': 'ruby', 'php': 'php', 'txt': 'plaintext',
    };
    return langMap[ext || ''] || 'plaintext';
  }

  private stripFrontmatter(markdown: string): string {
    if (!markdown.startsWith('---')) return markdown;
    const parts = markdown.split('---');
    return parts.length >= 3 ? parts.slice(2).join('---').trim() : markdown;
  }

  render(content: string, options: RenderOptions = {}): string {
    try {
      const mode = options.mode || 'auto';
      const filename = options.filename || '';
      const language = options.language || this.detectLanguage(filename);

      if (mode === 'auto') {
        if (filename.endsWith('.md')) {
          const pref = this.getMarkdownPreference();
          return pref === 'preview'
            ? this.renderMarkdownPreview(content)
            : this.renderSyntaxHighlight(content, 'markdown', options.showLineNumbers);
        }
        return this.renderSyntaxHighlight(content, language, options.showLineNumbers);
      }

      if (mode === 'markdown-preview') {
        return this.renderMarkdownPreview(content);
      }

      if (mode === 'markdown-source') {
        return this.renderSyntaxHighlight(content, 'markdown', options.showLineNumbers);
      }

      return this.renderSyntaxHighlight(content, language, options.showLineNumbers);
    } catch (error) {
      console.error('[ContentRenderer] Render failed:', error);
      return `<pre class="text-xs">${window.escapeHtml(content)}</pre>`;
    }
  }

  private renderMarkdownPreview(markdown: string): string {
    if (!window.marked) {
      console.warn('[ContentRenderer] marked.js not loaded, falling back to source');
      return this.renderSyntaxHighlight(markdown, 'markdown');
    }
    const body = this.stripFrontmatter(markdown);
    return `<div class="prose prose-invert max-w-none">${sanitizeRenderedHtml(String(window.marked.parse(body)))}</div>`;
  }

  private renderSyntaxHighlight(code: string, language: string, showLineNumbers?: boolean): string {
    if (!window.Prism) {
      console.warn('[ContentRenderer] Prism.js not loaded, falling back to plain text');
      return `<pre class="text-xs">${window.escapeHtml(code)}</pre>`;
    }

    const lines = code.split('\n').length;
    const useLineNumbers = showLineNumbers !== false && lines <= ContentRenderer.MAX_LINES_FOR_LINE_NUMBERS;
    const lineNumberClass = useLineNumbers ? ' line-numbers' : '';

    const grammar = window.Prism.languages[language] || window.Prism.languages.plaintext;
    const highlighted = window.Prism.highlight(code, grammar, language);

    return `<pre class="language-${language}${lineNumberClass}"><code class="language-${language}">${highlighted}</code></pre>`;
  }

  createMarkdownToggle(): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'btn-ghost text-xs';
    button.textContent = this.getMarkdownPreference() === 'preview' ? 'View Source' : 'View Preview';
    button.onclick = () => {
      const newMode = this.getMarkdownPreference() === 'preview' ? 'source' : 'preview';
      this.setMarkdownPreference(newMode);
      button.textContent = newMode === 'preview' ? 'View Source' : 'View Preview';
      window.dispatchEvent(new CustomEvent('markdown-mode-changed', { detail: { mode: newMode } }));
    };
    return button;
  }
}

window.ContentRenderer = new ContentRenderer();
